import PropTypes from "prop-types";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {Box, Breadcrumbs, Chip, IconButton, Typography} from "@mui/material";
import {useRef, useState} from "react";
import ComingSoon from "../../../../components/ComingSoon";

const ACCENT = "#0B5CAD";
const PLAYER_BG = "#161B22";

/** Joins labels as "A, B, C and D". */
const joinLabels = (labels) =>
  labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels[0] || "";

/** Formats seconds as m:ss. */
const formatDuration = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

/**
 * Inline tutorial video player: a dark cover with heading, duration and a play
 * button that is replaced by the native controls once playback starts.
 *
 * @param {{title: string, src: string}} video - Video to play.
 * @param {string} heading - Text shown at the top-left of the cover.
 * @param {string} [caption] - Optional text shown above the progress track.
 */
const TutorialVideoPlayer = ({ video, heading, caption }) => {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [duration, setDuration] = useState(null);
  const [failed, setFailed] = useState(false);

  const handlePlay = () => {
    setStarted(true);
    videoRef.current?.play()?.catch(() => {});
  };

  return (
    <Box
      sx={{
        // Fills the height left under the page header so the panel never scrolls;
        // the video is letterboxed inside instead of forcing a 16:9 box.
        position: "relative",
        flex: 1,
        minHeight: 200,
        width: "100%",
        bgcolor: PLAYER_BG,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <video
        ref={videoRef}
        src={video.src}
        title={video.title}
        preload="metadata"
        controls={started && !failed}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onError={() => setFailed(true)}
        style={{ width: "100%", height: "100%", display: "block", objectFit: "contain", outline: "none" }}
      />

      {(!started || failed) && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: PLAYER_BG,
            color: "white",
            display: "flex",
            flexDirection: "column",
            p: 2.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Typography variant="body2">{heading}</Typography>
            {Number.isFinite(duration) && (
              <Box sx={{ bgcolor: ACCENT, borderRadius: 5, px: 1, py: 0.25, fontSize: "0.7rem", flexShrink: 0 }}>
                {formatDuration(duration)}
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box
              sx={{
                // Sized by height so the decorative circle shrinks with a short player.
                height: "90%",
                maxHeight: 400,
                maxWidth: "100%",
                aspectRatio: "1",
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.03)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {failed ? (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", textAlign: "center", px: 2 }}>
                  This video could not be loaded. Please try again later.
                </Typography>
              ) : (
                <IconButton
                  aria-label={`Play ${video.title}`}
                  onClick={handlePlay}
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "#F2F4F7",
                    color: ACCENT,
                    "&:hover": { bgcolor: "white" },
                  }}
                >
                  <PlayArrowRoundedIcon sx={{ fontSize: 40 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {caption && (
            <Typography variant="caption" sx={{ textAlign: "center", color: "rgba(255,255,255,0.7)", mb: 2 }}>
              {caption}
            </Typography>
          )}
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: "rgba(255,255,255,0.3)" }} />
        </Box>
      )}
    </Box>
  );
};

TutorialVideoPlayer.propTypes = {
  video: PropTypes.shape({
    title: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  }).isRequired,
  heading: PropTypes.string.isRequired,
  caption: PropTypes.string,
};

/**
 * Tutorial page for a sidebar item. For a fused menu (`subIntents` given) it
 * lists the sub-intents as chips and plays one shared "process overview"
 * video; otherwise it plays the intent's own video(s).
 *
 * @param {string} title - Sidebar label, used for the breadcrumb and heading.
 * @param {Array<{label: string}>} [subIntents] - Intents covered by a fused menu's video.
 * @param {Array<{title: string, src: string}>} [videos] - Videos to show; ComingSoon when empty.
 */
const TutorialVideoView = ({ title, subIntents, videos }) => {
  const isFused = Boolean(subIntents);
  const labels = isFused ? joinLabels(subIntents.map((intent) => intent.label)) : "";
  // A fused menu shares a single video across all of its sub-intents.
  const videosToShow = (isFused ? videos?.slice(0, 1) : videos) || [];

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2, fontSize: "0.8rem" }}>
        <Typography variant="caption" color="text.secondary">Tutorial</Typography>
        <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 600 }}>{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2.5 }}>{title}</Typography>

      {isFused && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            This video covers the following sub-intents:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            {subIntents.map((intent) => (
              <Chip
                key={intent.label}
                label={intent.label}
                size="small"
                sx={{
                  borderRadius: 1,
                  height: 30,
                  px: 0.5,
                  bgcolor: "#EEF4FC",
                  border: "1px solid #D6E4F5",
                  color: "#1F2A37",
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            This video covers the same core process for {labels}. Entry points or input fields may vary.
          </Typography>
        </>
      )}

      {videosToShow.length ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          {videosToShow.map((video) => (
            <TutorialVideoPlayer
              key={video.src}
              video={video}
              heading={isFused ? "Process Overview Video" : video.title}
              caption={isFused ? `Same walkthrough for ${labels}` : undefined}
            />
          ))}
        </Box>
      ) : (
        <ComingSoon />
      )}
    </Box>
  );
};

TutorialVideoView.propTypes = {
  title: PropTypes.string,
  subIntents: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    })
  ),
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
    })
  ),
};

export default TutorialVideoView;
